import React, { useEffect, useRef, useState } from 'react';
import {
  VideoMuteButton,
  kickFocusedPlayback,
  useVideosMuted,
} from './GumletPlayer';
import { getVideosMuted } from '../services/videoMute';
import { getJinglePlaying, subscribeJinglePlaying } from '../services/jinglePlayback';
import { markAudioUnlocked } from '../services/audioUnlock';
import './IslandFeed.scss';

const IslandFeed = ({ children, initialIndex = 0, bgAudioSrc }) => {
  const containerRef = useRef(null);
  const bgAudioRef = useRef(null);
  const bgAudioStarting = useRef(false);
  const [activeIndex, setActiveIndex] = useState(() => initialIndex);
  const [loadedIndexes, setLoadedIndexes] = useState(() => new Set([0, 1]));
  const hasAppliedFocus = useRef(false);
  const childCount = React.Children.count(children);
  const [muted] = useVideosMuted();
  const [jinglePlaying, setJinglePlayingState] = useState(getJinglePlaying);

  useEffect(() => subscribeJinglePlaying(setJinglePlayingState), []);

  // Single source of truth for the shared background track: the video on
  // the intro slide (index 0) has its own sound, every other slide's video
  // is force-muted and relies on this track instead. Kept in one place so
  // no two effects can independently flip audio.muted and race each other.
  // `bgAudioStarting` guards against React (StrictMode, or any rapid
  // re-render) re-entering this while a play() is still settling — flipping
  // .muted or calling .pause() mid-flight is what throws the "play()
  // request was interrupted by a call to pause()" AbortError.
  // The island track and the header jingle must never be audible together
  // either, so the jingle playing is treated just like being on the intro
  // slide: pause (not restart) and resume once it stops.
  const syncBgAudio = () => {
    const audio = bgAudioRef.current;
    if (!audio || !bgAudioSrc) return;

    if (activeIndex === 0 || jinglePlaying) {
      bgAudioStarting.current = false;
      audio.pause();
      audio.muted = true;
      return;
    }

    if (bgAudioStarting.current) return;

    if (audio.paused) {
      bgAudioStarting.current = true;
      audio.muted = true;
      const playPromise = audio.play();
      const onSettled = () => { bgAudioStarting.current = false; };
      if (playPromise?.then) {
        playPromise.then(() => {
          audio.muted = getVideosMuted();
          onSettled();
        }).catch(onSettled);
      } else {
        audio.addEventListener('playing', () => {
          audio.muted = getVideosMuted();
          onSettled();
        }, { once: true });
      }
      return;
    }

    audio.muted = getVideosMuted();
  };

  const syncBgAudioRef = useRef(syncBgAudio);
  syncBgAudioRef.current = syncBgAudio;

  useEffect(() => {
    syncBgAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, muted, bgAudioSrc, jinglePlaying]);

  const handleGesture = (event) => {
    kickFocusedPlayback(event);
    syncBgAudio();
  };

  // Browsers only allow audio to become audible after a real "tap" gesture —
  // a swipe that scrolls the page never qualifies (this is intentional
  // mobile-browser behavior, not fixable from our side), and each
  // cross-origin Gumlet iframe needs its own qualifying gesture, separate
  // from our own <audio> element and from every other iframe. So instead of
  // unlocking once, keep retrying on every real tap anywhere on the page for
  // as long as the feed is mounted — whichever tap lands cleanly (not as
  // part of a swipe) unmutes the background track and the currently active
  // video, whatever that happens to be at that moment. Goes through a ref so
  // it always calls the latest syncBgAudio.
  useEffect(() => {
    if (!bgAudioSrc) return undefined;
    const unlock = (event) => {
      kickFocusedPlayback(event);
      syncBgAudioRef.current();
    };
    // A native 'click' is only ever synthesized for a genuine tap — never
    // for a touch sequence that ended up scrolling the page — so it's the
    // one reliable signal that a qualifying gesture actually happened, used
    // here purely to drive the mute button's "is sound actually unlocked
    // yet" indicator (separate from the user's mute/unmute preference).
    const onClick = (event) => {
      unlock(event);
      markAudioUnlocked();
    };
    const options = { capture: true };
    document.addEventListener('pointerdown', unlock, options);
    document.addEventListener('pointerup', unlock, options);
    document.addEventListener('keydown', unlock, options);
    document.addEventListener('click', onClick, options);
    return () => {
      document.removeEventListener('pointerdown', unlock, options);
      document.removeEventListener('pointerup', unlock, options);
      document.removeEventListener('keydown', unlock, options);
      document.removeEventListener('click', onClick, options);
    };
  }, [bgAudioSrc]);

  useEffect(() => {
    setLoadedIndexes((prev) => {
      const next = new Set(prev);
      [activeIndex - 1, activeIndex, activeIndex + 1].forEach((index) => {
        if (index >= 0) next.add(index);
      });
      return next;
    });
  }, [activeIndex]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateActive = () => {
      const slides = container.querySelectorAll('.island-slide');
      if (!slides.length) return;
      const center = container.scrollTop + container.clientHeight / 2;
      let closest = 0;
      let closestDist = Infinity;
      slides.forEach((slide) => {
        const index = Number(slide.dataset.index);
        const top = slide.offsetTop - slides[0].offsetTop;
        const mid = top + slide.offsetHeight / 2;
        const dist = Math.abs(center - mid);
        if (dist < closestDist) {
          closestDist = dist;
          closest = index;
        }
      });
      setActiveIndex((prev) => (prev === closest ? prev : closest));
    };

    container.addEventListener('scroll', updateActive, { passive: true });
    return () => container.removeEventListener('scroll', updateActive);
  }, [childCount]);

  useEffect(() => {
    if (hasAppliedFocus.current) return;
    const container = containerRef.current;
    const slides = container?.querySelectorAll('.island-slide');
    if (!slides?.length || slides.length <= initialIndex) return;
    if (initialIndex === 0 && slides.length === 1) return;

    hasAppliedFocus.current = true;
    if (initialIndex === 0) return;

    setActiveIndex(initialIndex);
    const previousBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = 'auto';
    container.scrollTop = slides[initialIndex].offsetTop - slides[0].offsetTop;
    container.style.scrollBehavior = previousBehavior;
  }, [initialIndex, childCount]);

  return (
    <div className="island-feed-shell">
      <div
        className="island-feed"
        ref={containerRef}
        onPointerDown={handleGesture}
        onTouchStart={handleGesture}
        onPointerUp={handleGesture}
        onTouchEnd={handleGesture}
      >
        {React.Children.map(children, (child, index) => (
          React.isValidElement(child)
            ? React.cloneElement(child, {
              isActive: index === activeIndex,
              shouldLoad: loadedIndexes.has(index) || Math.abs(index - activeIndex) <= 1,
              index,
            })
            : child
        ))}
      </div>
      {bgAudioSrc && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio ref={bgAudioRef} src={bgAudioSrc} loop preload="auto" />
      )}
      {childCount > 1 && (
        <div className="island-feed-dots" aria-hidden="true">
          {Array.from({ length: childCount }, (_, index) => (
            <span
              key={index}
              className={`island-feed-dot${index === activeIndex ? ' island-feed-dot--active' : ''}`}
            />
          ))}
        </div>
      )}
      <VideoMuteButton className="video-mute-button--feed" />
    </div>
  );
};

export const IslandSlide = ({
  children,
  icon,
  title,
  onClick,
  isActive,
  shouldLoad,
  index,
  forceMutedAudio,
}) => {
  const startY = useRef(0);
  const isClickable = Boolean(onClick);

  return (
    <div
      className={`island-slide${isActive ? ' island-slide--active' : ''}${isClickable ? '' : ' island-slide--static'}`}
      data-index={index}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onPointerDown={isClickable ? (event) => {
        startY.current = event.clientY;
      } : undefined}
      onClick={isClickable ? (event) => {
        if (Math.abs(event.clientY - startY.current) > 8) return;
        onClick(event);
      } : undefined}
      onKeyDown={isClickable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onClick(event);
        }
      } : undefined}
    >
      <div className="island-slide-video">
        {React.Children.map(children, (child) => (
          React.isValidElement(child)
            ? React.cloneElement(child, {
              isActive, shouldLoad, fill: true, forceMuted: forceMutedAudio,
            })
            : child
        ))}
      </div>
      {(icon || title) && (
        <div className="island-slide-overlay">
          {icon && <div className="island-slide-icon">{icon}</div>}
          {title && (
            <div className="island-slide-title">
              <span>{title}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IslandFeed;
